import styled from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../../../formInputsV2/styles/tokens';
import {
  AiChatButton,
  AquistaButton,
  CompanyInfoElements,
  CompanyInformationContainer,
  LogoContainer,
  LogoPriceContainer,
  Price,
  PriceContainer,
  QuoteInformationContainer,
  QuoteInformationItem,
  SavedButton,
} from './QuotationCard.styles';

// v2 look for the selectable risk-guarantee card in the result-page modals
// (RiskCard and riskCardReusable/*). Same visual language as the v2
// QuotationCard: the pieces that are identical are re-exported from
// QuotationCard.styles, only the layout is specific to this card.
// The old Cards/RiskCard.styles.js stays untouched — RiskCardSelected and two
// info modals still use it.
export {
  AiChatButton,
  AquistaButton,
  CompanyInfoElements,
  CompanyInformationContainer,
  LogoContainer,
  LogoPriceContainer,
  Price,
  PriceContainer,
  QuoteInformationContainer,
  QuoteInformationItem,
  SavedButton,
};

// LeftCol only groups the markup; its children take part in the card's grid
// directly (display: contents), so the AI button can sit in the top-right
// next to the logo/price while the action button sits beside the details.
export const LeftCol = styled.div`
  display: contents;
`;

export const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 12px;
  min-width: 190px;
`;

export const AquistaButtonsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: 100%;
`;

// Grid, dense: every item only states its column, and each one falls into the
// first row where that column is free. That keeps the AI button on the same
// row as the logo/price with or without the semestrale warning above them,
// and lets the action button (spanning many rows) end at the bottom of the
// details whatever their number.
export const RiskCardStyles = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-auto-flow: dense;
  align-items: start;
  column-gap: 24px;
  height: 100%;
  padding: 20px 24px;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.disabledBorder};
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.05);
  font-family: ${v2FontFamily};
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(16, 24, 40, 0.09);
  }

  &.selected {
    border-color: ${v2Colors.primary};
    box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
  }

  /* Some cards (RiskCardPrima) wrap LeftCol/RightCol in a plain div */
  .row-container {
    display: contents;
  }

  /* Semestrale warning (Linear / Genial Più) */
  ${LeftCol} > .alert {
    grid-column: 1 / -1;
    margin: 0 0 12px !important;
    padding: 8px 12px !important;
    border: 1px solid ${v2Colors.warningBorder};
    border-radius: 10px;
    background: ${v2Colors.warningSoft};
    color: ${v2Colors.warningText};
    font-size: 12.5px;
    line-height: 1.4;
  }

  /* "Servizio non assicurativo" (My Rescue) — column 1 only (not the full
     row like the semestrale warning above), so the grid's dense packing
     puts the AI chat button beside it in column 2, same row. */
  ${LeftCol} > .alert.alert-danger {
    grid-column: 1;
    justify-self: start;
    width: fit-content;
    border-color: ${v2Colors.dangerBorder};
    background: ${v2Colors.dangerSoft};
    color: ${v2Colors.danger};
    font-weight: 600;
  }

  ${AiChatButton} {
    grid-column: 2;
    justify-self: end;
    align-self: start;
  }

  ${LogoPriceContainer} {
    grid-column: 1;
  }

  ${CompanyInformationContainer} {
    grid-column: 1;
    margin-top: 14px;
    margin-bottom: 0;
  }

  /* ProfessionRiskCard always renders this row, even with no links in it */
  ${CompanyInformationContainer}:empty {
    display: none;
  }

  ${QuoteInformationContainer} {
    grid-column: 1;
    margin-top: 12px;
    /* undo the stray Bootstrap "ms-3" some cards put on this row */
    margin-left: 0 !important;
  }

  /* wrapper some cards put around the action button */
  .mobile-width {
    width: 100%;
  }

  /* Bootstrap form controls some cards render inside RightCol (Travel: beneficiary) */
  ${RightCol} .form-label {
    margin-bottom: 4px;
    color: ${v2Colors.textMuted};
    font-size: 12px;
    font-weight: 500;
  }

  ${RightCol} .form-select,
  ${RightCol} .form-control {
    min-height: 40px;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 12px;
    border: 1px solid ${v2Colors.border};
    border-radius: 10px;
    color: ${v2Colors.text};
    font-family: ${v2FontFamily};
    font-size: 14px;

    &:focus {
      border-color: ${v2Colors.primary};
      box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
    }
  }

  ${RightCol} .mb-3 {
    margin-bottom: 12px !important;
  }

  ${RightCol} {
    grid-column: 2;
    grid-row: span 10;
    align-self: end;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px;
    column-gap: 12px;

    ${CompanyInformationContainer},
    ${QuoteInformationContainer} {
      grid-column: 1 / -1;
    }

    ${RightCol} {
      grid-column: 1 / -1;
      grid-row: auto;
      align-self: stretch;
      min-width: 0;
      margin-top: 14px;
    }
  }
`;
